import React, { useState, useEffect, useCallback } from 'react';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function formatJson(text) {
  const parsed = JSON.parse(text);
  return JSON.stringify(parsed, null, 2);
}

export default function AdminGuidedTour() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [erro, setErro] = useState('');
  const [textarea, setTextarea] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const data = await apiAdmin.guidedTourGet();
      const effective = data?.effective ?? data;
      setTextarea(JSON.stringify(effective, null, 2));
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(getDisplayMessage(err, 'Erro ao carregar tour guiado.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleFormatar = () => {
    setErro('');
    try {
      setTextarea(formatJson(textarea));
      toast.success('JSON formatado.');
    } catch {
      setErro('JSON inválido: não foi possível formatar.');
    }
  };

  const handleSalvar = async () => {
    setSaving(true);
    setErro('');
    try {
      const body = JSON.parse(textarea);
      const res = await apiAdmin.guidedTourPut(body);
      if (res?.effective) {
        setTextarea(JSON.stringify(res.effective, null, 2));
      }
      toast.success('Tour guiado guardado.');
    } catch (err) {
      if (err instanceof SyntaxError) {
        setErro('JSON inválido: corrija a sintaxe antes de guardar.');
        return;
      }
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(getDisplayMessage(err, 'Erro ao guardar.'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Repor textos do tour aos valores padrão da plataforma? A personalização na base de dados será removida.')) {
      return;
    }
    setResetting(true);
    setErro('');
    try {
      const res = await apiAdmin.guidedTourReset();
      if (res?.effective) {
        setTextarea(JSON.stringify(res.effective, null, 2));
      }
      toast.success('Tour reposto aos padrões.');
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(getDisplayMessage(err, 'Erro ao repor.'));
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-slate-600">
        A carregar…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Tour guiado</h1>
        <p className="mt-1 text-sm text-slate-600">
          Edite o JSON do tour (módulos e passos com <code className="rounded bg-slate-100 px-1">selector</code>,{' '}
          <code className="rounded bg-slate-100 px-1">title</code>, <code className="rounded bg-slate-100 px-1">body</code>).
          Os utilizadores da aplicação veem a versão mesclada com os textos padrão para módulos não personalizados.
        </p>
      </div>

      {erro ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{erro}</div>
      ) : null}

      <textarea
        className="min-h-[480px] w-full rounded-lg border border-slate-300 bg-white p-4 font-mono text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        spellCheck={false}
        value={textarea}
        onChange={(e) => setTextarea(e.target.value)}
        aria-label="JSON do tour guiado"
      />

      <div className="flex flex-wrap gap-3">
        <Button type="button" variant="secondary" onClick={handleFormatar}>
          Formatar JSON
        </Button>
        <Button type="button" onClick={handleSalvar} disabled={saving}>
          {saving ? 'A guardar…' : 'Guardar'}
        </Button>
        <Button type="button" variant="outline" onClick={handleReset} disabled={resetting}>
          {resetting ? 'A repor…' : 'Restaurar padrões'}
        </Button>
        <Button type="button" variant="ghost" onClick={carregar}>
          Recarregar
        </Button>
      </div>
    </div>
  );
}
